import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Alert,
  AlertDescription,
} from '@roger/ui-components';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/shallow';
import { useToast } from '../../hooks/useToastContext';
import { getVSP, updateVSP, type VSPData } from '../../services/rogerDocApi';

const DEFAULT_VSP: VSPData = {
  c485np_temphigh: null,
  c485np_templow: null,
  c485np_pulsehigh: null,
  c485np_pulselow: null,
  c485np_resphigh: null,
  c485np_resplow: null,
  c485np_syshigh: null,
  c485np_syslow: null,
  c485np_diashigh: null,
  c485NP_DiasLow: null,
  c485np_02stat: null,
  c485np_fastbslevelgt: null,
  c485np_fastbslevellt: null,
  c485np_randombslevelgt: null,
  c485np_randombslevellt: null,
};

// Validation ranges for vital signs (reasonably permissive to avoid false positives)
const VALIDATION_RANGES: Record<
  string,
  { min: number; max: number; label: string }
> = {
  // Temperature (°F) - hypothermia to high fever range
  c485np_temphigh: { min: 95, max: 115, label: 'Temperature High' },
  c485np_templow: { min: 90, max: 105, label: 'Temperature Low' },
  // Pulse (bpm) - bradycardia to severe tachycardia
  c485np_pulsehigh: { min: 40, max: 250, label: 'Pulse High' },
  c485np_pulselow: { min: 20, max: 120, label: 'Pulse Low' },
  // Respirations (/min)
  c485np_resphigh: { min: 10, max: 60, label: 'Respirations High' },
  c485np_resplow: { min: 4, max: 30, label: 'Respirations Low' },
  // Systolic BP (mmHg)
  c485np_syshigh: { min: 80, max: 280, label: 'Systolic BP High' },
  c485np_syslow: { min: 60, max: 180, label: 'Systolic BP Low' },
  // Diastolic BP (mmHg)
  c485np_diashigh: { min: 40, max: 180, label: 'Diastolic BP High' },
  c485NP_DiasLow: { min: 30, max: 120, label: 'Diastolic BP Low' },
  // O2 Saturation (%) - must be 0-100
  c485np_02stat: { min: 0, max: 100, label: 'O2 Saturation' },
  // Blood Sugar (mg/dL) - hypoglycemia to severe hyperglycemia
  c485np_fastbslevelgt: {
    min: 50,
    max: 600,
    label: 'Fasting Blood Sugar High',
  },
  c485np_fastbslevellt: { min: 30, max: 200, label: 'Fasting Blood Sugar Low' },
  c485np_randombslevelgt: {
    min: 50,
    max: 600,
    label: 'Random Blood Sugar High',
  },
  c485np_randombslevellt: {
    min: 30,
    max: 300,
    label: 'Random Blood Sugar Low',
  },
};

// High/Low pairs for logical validation
const HIGH_LOW_PAIRS: Array<{
  high: keyof VSPData;
  low: keyof VSPData;
  label: string;
}> = [
  { high: 'c485np_temphigh', low: 'c485np_templow', label: 'Temperature' },
  { high: 'c485np_pulsehigh', low: 'c485np_pulselow', label: 'Pulse' },
  { high: 'c485np_resphigh', low: 'c485np_resplow', label: 'Respirations' },
  { high: 'c485np_syshigh', low: 'c485np_syslow', label: 'Systolic BP' },
  { high: 'c485np_diashigh', low: 'c485NP_DiasLow', label: 'Diastolic BP' },
  {
    high: 'c485np_fastbslevelgt',
    low: 'c485np_fastbslevellt',
    label: 'Fasting Blood Sugar',
  },
  {
    high: 'c485np_randombslevelgt',
    low: 'c485np_randombslevellt',
    label: 'Random Blood Sugar',
  },
];

// Vital signs configuration for the form UI
const VITAL_SIGNS_CONFIG: Array<{
  label: string;
  highField: keyof VSPData;
  lowField?: keyof VSPData;
  step?: string;
  unit?: string;
}> = [
  {
    label: 'Temperature',
    highField: 'c485np_temphigh',
    lowField: 'c485np_templow',
    step: '0.1',
    unit: '°F',
  },
  {
    label: 'Pulse',
    highField: 'c485np_pulsehigh',
    lowField: 'c485np_pulselow',
    unit: 'bpm',
  },
  {
    label: 'Respirations',
    highField: 'c485np_resphigh',
    lowField: 'c485np_resplow',
    unit: '/min',
  },
  {
    label: 'Systolic BP',
    highField: 'c485np_syshigh',
    lowField: 'c485np_syslow',
    unit: 'mmHg',
  },
  {
    label: 'Diastolic BP',
    highField: 'c485np_diashigh',
    lowField: 'c485NP_DiasLow',
    unit: 'mmHg',
  },
  {
    label: 'O2 Saturation',
    highField: 'c485np_02stat',
    unit: '%',
  },
  {
    label: 'Fasting Blood Sugar',
    highField: 'c485np_fastbslevelgt',
    lowField: 'c485np_fastbslevellt',
    unit: 'mg/dL',
  },
  {
    label: 'Random Blood Sugar',
    highField: 'c485np_randombslevelgt',
    lowField: 'c485np_randombslevellt',
    unit: 'mg/dL',
  },
];

const validateVSPData = (data: VSPData): string[] => {
  const warnings: string[] = [];

  // Check each field against its range
  for (const [field, range] of Object.entries(VALIDATION_RANGES)) {
    const value = data[field as keyof VSPData];
    if (value !== null && value !== undefined && value !== 0) {
      if (value < range.min || value > range.max) {
        warnings.push(
          `${range.label}: ${value} is outside typical range (${range.min}-${range.max})`
        );
      }
    }
  }

  // Check high/low logical consistency
  for (const pair of HIGH_LOW_PAIRS) {
    const highVal = data[pair.high];
    const lowVal = data[pair.low];
    if (
      highVal !== null &&
      lowVal !== null &&
      highVal !== 0 &&
      lowVal !== 0 &&
      highVal <= lowVal
    ) {
      warnings.push(
        `${pair.label}: High threshold (${highVal}) should be greater than low threshold (${lowVal})`
      );
    }
  }

  return warnings;
};

// Moved outside to prevent re-creation on every render
const ThresholdInput: React.FC<{
  field: keyof VSPData;
  value: string;
  step?: string;
  onChange: (field: keyof VSPData, value: string) => void;
  onSetZero: (field: keyof VSPData) => void;
  onClear: (field: keyof VSPData) => void;
}> = ({ field, value, step, onChange, onSetZero, onClear }) => (
  <div className='flex items-center gap-2'>
    <Input
      type='number'
      value={value}
      onChange={e => onChange(field, e.target.value)}
      step={step}
      className='w-24'
      inputSize='sm'
    />
    <Button
      type='button'
      size='sm'
      variant='outline'
      onClick={() => onSetZero(field)}
      className='text-xs px-2'
    >
      Set 0
    </Button>
    <Button
      type='button'
      size='sm'
      variant='outline'
      onClick={() => onClear(field)}
      className='text-xs px-2 text-muted-foreground'
    >
      Clear
    </Button>
  </div>
);

const VSPManagement: React.FC = () => {
  const { user } = useStore(
    useShallow(state => ({
      user: state.user,
    }))
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [vspData, setVspData] = useState<VSPData | null>(null);
  const { success, error } = useToast();

  const agencySystemName = user?.agency?.system_name;

  // Compute validation warnings whenever vspData changes
  const validationWarnings = useMemo(() => {
    if (!vspData) return [];
    return validateVSPData(vspData);
  }, [vspData]);

  // Fetch VSP data on mount when agency is available
  useEffect(() => {
    const fetchVSP = async () => {
      setIsLoading(true);
      try {
        const data = await getVSP();
        setVspData({
          ...DEFAULT_VSP,
          ...data,
        });
      } catch (err) {
        error(
          'Fetch Failed',
          err instanceof Error ? err.message : 'Failed to fetch VSP data'
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (agencySystemName) {
      fetchVSP();
    }
  }, [agencySystemName, error]);

  const handleUpdateVSP = async () => {
    if (!agencySystemName || !vspData) return;

    setIsSaving(true);
    try {
      await updateVSP(vspData);
      success('Success', 'VSP data updated successfully');
    } catch (err) {
      error(
        'Update Failed',
        err instanceof Error ? err.message : 'Failed to update VSP data'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof VSPData, value: string) => {
    const processedValue = value === '' ? null : parseFloat(value);
    setVspData(prev => (prev ? { ...prev, [field]: processedValue } : null));
  };

  const handleSetZero = (field: keyof VSPData) => {
    setVspData(prev => (prev ? { ...prev, [field]: 0 } : null));
  };

  const handleClear = (field: keyof VSPData) => {
    setVspData(prev => (prev ? { ...prev, [field]: null } : null));
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  // Show loading state while fetching initial data
  if (isLoading && !vspData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vital Signs Parameters</CardTitle>
          <CardDescription>
            Configure physician notification thresholds for vital signs
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex justify-between items-start'>
          <div>
            <CardTitle>Vital Signs Parameters</CardTitle>
            <CardDescription>
              Configure physician notification thresholds for vital signs
            </CardDescription>
          </div>
          {vspData && (
            <Button
              size='sm'
              onClick={handleUpdateVSP}
              disabled={isSaving || validationWarnings.length > 0}
            >
              {isSaving ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Saving...
                </>
              ) : (
                'Update VSP'
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {/* Validation Warnings */}
        {validationWarnings.length > 0 && (
          <Alert className='border-yellow-200 bg-yellow-50'>
            <AlertTriangle className='h-4 w-4 text-yellow-600' />
            <AlertDescription>
              <div className='ml-2'>
                <p className='font-medium text-yellow-800 mb-1'>
                  Validation warnings ({validationWarnings.length}):
                </p>
                <ul className='text-sm text-yellow-700 list-disc list-inside space-y-0.5'>
                  {validationWarnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* VSP Form */}
        {vspData && (
          <TooltipProvider>
            <div className='rounded-md border'>
              <div className='bg-muted/50 px-4 py-2 border-b'>
                <span className='text-sm font-medium'>
                  Notify physician when:
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-[180px]'>Vital Sign</TableHead>
                    <TableHead>Above</TableHead>
                    <TableHead>Below</TableHead>
                    <TableHead className='w-[80px]'>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {VITAL_SIGNS_CONFIG.map(config => (
                    <TableRow key={config.label}>
                      <TableCell className='font-medium'>
                        {config.label}
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='text-muted-foreground font-medium cursor-help'>
                                &gt;
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Notify when value is above this threshold</p>
                            </TooltipContent>
                          </Tooltip>
                          <ThresholdInput
                            field={config.highField}
                            value={getDisplayValue(vspData[config.highField])}
                            step={config.step}
                            onChange={handleInputChange}
                            onSetZero={handleSetZero}
                            onClear={handleClear}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {config.lowField ? (
                          <div className='flex items-center gap-2'>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='text-muted-foreground font-medium cursor-help'>
                                  &lt;
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Notify when value is below this threshold</p>
                              </TooltipContent>
                            </Tooltip>
                            <ThresholdInput
                              field={config.lowField}
                              value={getDisplayValue(vspData[config.lowField])}
                              step={config.step}
                              onChange={handleInputChange}
                              onSetZero={handleSetZero}
                              onClear={handleClear}
                            />
                          </div>
                        ) : (
                          <span className='text-muted-foreground text-sm'>
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className='text-muted-foreground text-sm'>
                        {config.unit || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TooltipProvider>
        )}

        {/* No agency state */}
        {!agencySystemName && (
          <div className='text-center py-8 text-muted-foreground'>
            No agency found. Please ensure you are logged in with an agency.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VSPManagement;
